/**
 * "PyMOL Session": the 3D scene as a `.pse` file for opening in desktop PyMOL.
 *
 * Only shown when the visualizer offers one (Tabs/index.jsx asks the seam), which today means
 * WASM PyMOL. It is an EXPORT, not a BMaps session: BMaps does not read a `.pse` back, so the
 * text points at Save Session for that.
 */
import { useState } from 'react';

// Material UI
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// Local
import { createBlobURL, downloadBlobURL, timestampedFilename } from '../../../ui_utils';
import { saveNativeSession } from '../../../../MainCanvas';

/** `BMaps-Session_20260922-1432.pse` */
export function sessionFilename(extension, now=new Date()) {
    return timestampedFilename('BMaps-Session', extension, now);
}

function sizeText(bytes) {
    if (!(bytes > 0)) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function download(blob, filename) {
    downloadBlobURL(createBlobURL(blob), filename);
}

export default function ExportSessionTab() {
    /** null | {busy: true} | {info: string} | {error: string} */
    const [status, setStatus] = useState(null);
    const busy = !!(status && status.busy);

    async function saveSession() {
        setStatus({ busy: true });
        try {
            const session = await saveNativeSession();
            // null = the viewer has no scene to write yet (the payload is still starting)
            if (!session || !session.blob) throw new Error('The 3D viewer has nothing to save yet.');
            const filename = sessionFilename(session.extension || 'pse');
            download(session.blob, filename);
            setStatus({ info: `${filename}${session.blob.size ? ` · ${sizeText(session.blob.size)}` : ''}` });
        } catch (ex) {
            console.error('Session export failed', ex);
            setStatus({ error: (ex && ex.message) || String(ex) });
        }
    }

    return (
        <Grid container sx={{ flexDirection: 'column', alignItems: 'center', gap: '1em' }} id="export-pane_session">
            <Typography variant="caption">PyMOL session (.pse) · Includes PyMOL™</Typography>
            <Typography align="center">
                The 3D workspace as a PyMOL session file: the molecules, representations, colors,
                surfaces, shapes, labels and the current view, for opening in desktop PyMOL.
            </Typography>
            <Button variant="contained" onClick={saveSession} disabled={busy}>
                {busy ? 'Saving…' : 'Save PyMOL session'}
            </Button>
            <Typography variant="caption" color={status && status.error ? 'error' : 'textSecondary'} align="center">
                {(status && status.error)
                    || (status && status.info)
                    || (busy ? 'Writing the session…' : 'BMaps cannot open a .pse again: for a session to restore here, use Save Session.')}
            </Typography>
        </Grid>
    );
}
